import { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import { Flex, Text, DropdownButton } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { globalEnv } from "~/global-env";
import { ExtendedInfoTooltip } from "~/pages/Compliance/Common/ExtendedInfoTooltip";
import { ProgramName } from "~/pages/Compliance/utils/constants";
import { toDbId } from "~/utils/graphql-helpers";
import { useStartRemediation } from "../../hooks";
import InviteInvestorModal from "../../InviteInvestorsModal";
import { PositionsTableRowItem } from "../../types";
import {
  ActionButton,
  RemediationActionContainer,
  StatusLeftSide,
  StatusRightSide,
  WarningTextColor,
} from "./styles_vanilla.css";

interface Props {
  data?: PositionsTableRowItem;
}

const RemediationRequired = ({ data }: Props) => {
  const [inviteModalOpen, setInviteModalOpen] = useState<boolean>(false);
  const programName = data?.program?.[0] as ProgramName | null;
  const portalStatus = data?.position?.portalStatus;
  const isPortalEnabled = portalStatus?.includes("VISIBLE") ?? false;

  const showRemediation = isPortalEnabled;

  const tooltipMessage =
    "Cannot send remediation request. The entity has not been made visible within the portal.";

  const positionId = data?.id;

  const history = useHistory();
  const currentParams = new URLSearchParams(history.location.search);

  const { startRemediation } = useStartRemediation();

  const handleInviteClick = useCallback(() => {
    if (programName === "Screening" && positionId && data?.client?.id && data?.entity?.id) {
      void startRemediation({
        positionId,
        arenaId: toDbId(data.client.id),
        entityId: toDbId(data.entity.id),
        programName: programName,
        emailAddresses: [],
      });
    } else {
      setInviteModalOpen(true);
    }
  }, [programName, positionId, data, startRemediation, setInviteModalOpen]);

  return (
    <>
      <Flex className={RemediationActionContainer}>
        <Flex className={StatusLeftSide}>
          <Icon type="exclamation-triangle" variant="outline" className={WarningTextColor} />
          <Text variant="strong-md" className={WarningTextColor}>
            Action Required
          </Text>
        </Flex>
        <Flex className={StatusRightSide}>
          <DropdownButton
            icon="ellipsis-h"
            variant="muted"
            buttonClassName={ActionButton}
            direction="down"
            usePortal
            align="right"
          >
            <DropdownButton.Action
              disabled={!showRemediation}
              onSelect={() => handleInviteClick()}
            >
              <ExtendedInfoTooltip message={!showRemediation ? tooltipMessage : ""}>
                <Text>Send remediation request</Text>
              </ExtendedInfoTooltip>
            </DropdownButton.Action>
            <DropdownButton.Action
              onSelect={() => {
                currentParams.set("positionId", String(positionId));
                history.push({
                  pathname: `${globalEnv.complianceRoute}/positions/link-case`,
                  search: currentParams.toString(),
                });
              }}
            >
              <Text>Link case to this position</Text>
            </DropdownButton.Action>
          </DropdownButton>
        </Flex>
      </Flex>
      {inviteModalOpen && programName && (
        <InviteInvestorModal
          positionId={positionId}
          arenaId={data?.client?.id}
          entityId={data?.entity?.id}
          programName={programName}
          accountName={data?.position?.accountName || ""}
          entityName={data?.entity?.name || ""}
          onClose={() => setInviteModalOpen(false)}
        />
      )}
    </>
  );
};

export default RemediationRequired;
